import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Chip,
  ToggleButton, ToggleButtonGroup, Tabs, Tab, Link, Divider, Stack,
  IconButton, Tooltip, Alert, CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PhoneIphone as MobileIcon,
  Business as BusinessIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import PageSearchBar from '../components/PageSearchBar';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const OFFICE_COLORS = {
  NY:  '#1565C0',
  NYC: '#1976D2',
  CT:  '#455A64',
  FL:  '#EF6C00',
  CA:  '#6A1B9A',
};

function CopyButton({ value, title }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <Tooltip title={copied ? 'Copied' : (title || 'Copy')}>
      <IconButton size="small" onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}>
        <CopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

// Sort helpers — case-insensitive, numeric-aware, stable.
function normForCompare(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v;
  const s = String(v).trim();
  // Phone/extension comparison: strip non-digits so (516)555-1212 sorts with 5165551212
  if (/^[\d\s().+\-x]+$/.test(s) && /\d/.test(s)) {
    const digits = s.replace(/\D/g, '');
    if (digits) return parseInt(digits.slice(0, 15), 10);
  }
  return s.toLowerCase();
}
function compareBy(a, b, key) {
  const av = normForCompare(a[key]);
  const bv = normForCompare(b[key]);
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}
function stableSort(arr, key, direction) {
  const sign = direction === 'desc' ? -1 : 1;
  return arr
    .map((item, idx) => [item, idx])
    .sort((x, y) => {
      const c = compareBy(x[0], y[0], key);
      return c !== 0 ? sign * c : x[1] - y[1];
    })
    .map((pair) => pair[0]);
}

export default function Directory() {
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  const [office, setOffice] = useState('ALL');
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sort state — default to last name ascending
  const [sortKey, setSortKey] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, metaRes, statsRes] = await Promise.all([
        api.get('/directory', { params: { limit: 1000 } }),
        api.get('/directory/meta'),
        api.get('/directory/stats'),
      ]);
      setEmployees(empRes.data.employees || []);
      setMeta(metaRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = employees.filter((e) => {
      if (office !== 'ALL' && (e.office || '').toUpperCase() !== office) return false;
      if (!q) return true;
      const hay = [
        e.first_name, e.last_name, e.email, e.title, e.department,
        e.dept_code, e.office, e.extension, e.direct_phone, e.mobile,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    return stableSort(rows, sortKey, sortDir);
  }, [employees, query, office, sortKey, sortDir]);

  // Flat list, no grouping. Department is a sortable column.


  // Header cell factory — every column sortable by default
  const sortableCell = (key, label) => (
    <TableCell
      sx={{ fontWeight: 700 }}
      sortDirection={sortKey === key ? sortDir : false}
    >
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'asc'}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const officeChips = useMemo(() => {
    if (!stats?.by_office) return [];
    return stats.by_office;
  }, [stats]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A5490' }}>
            ACS Directory
          </Typography>
          {meta?.effective_date && (
            <Typography variant="caption" color="text.secondary">
              Source: {meta.source}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadAll}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`People${stats?.total ? ` (${stats.total})` : ''}`} />
        <Tab label={`Offices${meta?.offices ? ` (${meta.offices.length})` : ''}`} />
        <Tab label="Help Desks & Shared Emails" />
      </Tabs>

      {tab === 0 && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
            <PageSearchBar
              placeholder="Search name, email, extension, phone, department…"
              value={query}
              onChange={(v) => setQuery(v)}
            />
            <ToggleButtonGroup
              value={office}
              exclusive
              onChange={(_, v) => v && setOffice(v)}
              size="small"
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="NY">NY</ToggleButton>
              <ToggleButton value="NYC">NYC</ToggleButton>
              <ToggleButton value="FL">FL</ToggleButton>
              <ToggleButton value="CA">CA</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {officeChips.length > 0 && (
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
              {officeChips.map((o) => (
                <Chip
                  key={o.office}
                  label={`${o.office}: ${o.count}`}
                  size="small"
                  sx={{
                    bgcolor: OFFICE_COLORS[(o.office || '').toUpperCase()] || '#607D8B',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          )}

          <Paper variant="outlined">
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {sortableCell('last_name', 'Name')}
                      {sortableCell('title', 'Title')}
                      {sortableCell('office', 'Office')}
                      {sortableCell('extension', 'Ext')}
                      {sortableCell('direct_phone', 'Phone')}
                      {sortableCell('mobile', 'Mobile')}
                      {sortableCell('email', 'Email')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No matches</TableCell></TableRow>
                    )}
                    {filtered.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {e.first_name} {e.last_name}
                        </TableCell>
                        <TableCell>
                          {e.title || '—'}
                        </TableCell>
                        <TableCell>
                          {e.office && (
                            <Chip
                              size="small"
                              label={e.office}
                              sx={{
                                bgcolor: OFFICE_COLORS[(e.office || '').toUpperCase()] || '#607D8B',
                                color: '#fff',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{e.extension || ''}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {e.direct_phone && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Link href={`tel:${e.direct_phone.replace(/\D/g,'')}`} underline="hover">
                                {e.direct_phone}
                              </Link>
                              <CopyButton value={e.direct_phone} />
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {e.mobile && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <MobileIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Link href={`tel:${e.mobile.replace(/\D/g,'')}`} underline="hover">
                                {e.mobile}
                              </Link>
                              <CopyButton value={e.mobile} />
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          {e.email && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Link href={`mailto:${e.email}`} underline="hover">
                                {e.email}
                              </Link>
                              <CopyButton value={e.email} />
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing {filtered.length} of {employees.length}
          </Typography>
        </>
      )}

      {tab === 1 && meta && (
        <Stack spacing={2}>
          {meta.offices.map((o, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <BusinessIcon sx={{ color: '#1A5490' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{o.name}</Typography>
              </Stack>
              {o.company && (
                <Typography variant="body2" color="text.secondary">{o.company}</Typography>
              )}
              {o.address && <Typography variant="body2">{o.address}</Typography>}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {o.toll_free    && <Typography variant="body2"><strong>Toll-free:</strong> <Link href={`tel:${o.toll_free.replace(/\D/g,'')}`}>{o.toll_free}</Link></Typography>}
                {o.office_phone && <Typography variant="body2"><strong>Office:</strong> <Link href={`tel:${o.office_phone.replace(/\D/g,'')}`}>{o.office_phone}</Link></Typography>}
                {o.fax          && <Typography variant="body2"><strong>Fax:</strong> {o.fax}</Typography>}
                {o.contact      && <Typography variant="body2"><strong>Contact:</strong> {o.contact}</Typography>}
                {o.contact_mobile && <Typography variant="body2"><strong>Mobile:</strong> <Link href={`tel:${o.contact_mobile.replace(/\D/g,'')}`}>{o.contact_mobile}</Link></Typography>}
                {o.contact_email && <Typography variant="body2"><strong>Email:</strong> <Link href={`mailto:${o.contact_email}`}>{o.contact_email}</Link></Typography>}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {tab === 2 && meta && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Help Desks & Service Lines</Typography>
            <Stack spacing={0.5}>
              {Object.entries(meta.help_desks || {}).map(([k, v]) => (
                <Typography key={k} variant="body2">
                  <strong>{k.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong>{' '}
                  <Link href={`tel:${String(v).replace(/\D/g,'')}`}>{v}</Link>
                </Typography>
              ))}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Shared Department Emails</Typography>
            <Stack spacing={0.5}>
              {(meta.shared_emails || []).map((s) => (
                <Typography key={s.address} variant="body2">
                  <Link href={`mailto:${s.address}`}>{s.address}</Link> — {s.purpose}
                </Typography>
              ))}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Phone System ({meta.phone_system?.system || '3CX'})</Typography>
            <Typography variant="body2">Voicemail dial: <strong>{meta.phone_system?.voicemail_dial || '999'}</strong></Typography>
            <Typography variant="body2">Default VM password: <strong>{meta.phone_system?.default_vm_password || '0694'}</strong></Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Department Codes</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.entries(meta.department_codes || {}).map(([code, label]) => (
                <Chip key={code} size="small" label={`${code} · ${label}`} sx={{ mb: 1 }} />
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
